import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import AppSnackbar from '../../../common/AppSnackbar';
import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import HazardPill from '../../hazards/components/HazardPill';
import { getHazardIdPrefix } from '../../hazards/Hazard.config';
import { useFindProductHazards } from '../../hazards/Hazard.hooks';
import { Hazard } from '../../hazards/Hazard.interface';
import { BaseProductEntityTableProps } from '../Product.interface';

const HazardsTable: React.FC<BaseProductEntityTableProps> = (
  props: BaseProductEntityTableProps
) => {
  const { productId, apiParams, fetchRef } = props;
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);

  const {
    productHazardList,
    isLoadingProductHazards,
    loadingProductHazardsError,
    findProductHazards
  } = useFindProductHazards(productId);

  const findProductHazardsWithParams = useCallback(() => {
    findProductHazards({ page, limit, order, direction, ...apiParams });
  }, [findProductHazards, page, limit, order, direction, apiParams]);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findProductHazardsWithParams;
    }
    findProductHazardsWithParams();
  }, [fetchRef, findProductHazardsWithParams]);

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'hazard.id'}
                  direction={getSortDirection('hazard.id')}
                  onClick={() => handleSort('hazard.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'hazard.category'}
                  direction={getSortDirection('hazard.category')}
                  onClick={() => handleSort('hazard.category')}
                >
                  Hazard Category
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'hazard.title'}
                  direction={getSortDirection('hazard.title')}
                  onClick={() => handleSort('hazard.title')}
                >
                  Hazardous Situation
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'hazard.initialProbability'}
                  direction={getSortDirection('hazard.initialProbability')}
                  onClick={() => handleSort('hazard.initialProbability')}
                >
                  Probability
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'hazard.initialSeverity'}
                  direction={getSortDirection('hazard.initialSeverity')}
                  onClick={() => handleSort('hazard.initialSeverity')}
                >
                  Severity
                </TableSortLabel>
              </TableCell>
              <TableCell>Initial RPN</TableCell>
              <TableCell>Residual RPN</TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductHazards && (
              <TableRow>
                <TableCell colSpan={8} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productHazardList &&
              productHazardList.data.map((hazard: Hazard) => (
                <TableRow
                  hover
                  key={hazard.id}
                  className={hazard.deleted ? 'deleted' : ''}
                  onClick={() =>
                    history.push(
                      `/portal/products/${productId}/hazards/${hazard.id}`
                    )
                  }
                >
                  <TableCell>{`${getHazardIdPrefix()}-${hazard.id}`}</TableCell>
                  <TableCell>{hazard.category}</TableCell>
                  <TableCell>{hazard.title}</TableCell>
                  <TableCell>
                    {hazard.initialProbability > 0 && (
                      <HazardPill
                        value={hazard.initialProbability}
                        type='probability'
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {hazard.initialSeverity > 0 && (
                      <HazardPill
                        value={hazard.initialSeverity}
                        type='severity'
                      />
                    )}
                  </TableCell>
                  <TableCell>{hazard.initialRpn}</TableCell>
                  <TableCell>{hazard.residualRpn}</TableCell>
                  <TableCell>
                    <TaskStatusLabel task={hazard.task} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={productHazardList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      <AppSnackbar severity='error' open={Boolean(loadingProductHazardsError)}>
        There was an error loading the list of Hazard.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default HazardsTable;
